// apps/client/src/components/reports/TrailBookChangeStatCard.tsx
// "Trail Book Change" stat card: the plain opening-versus-closing view of the
// trail book over the run-off window. Sits beside Portfolio Run-Off Rate on the
// Broker Trail Run-Off and Loan Trail Drop-Off pages.
//
// Why a second card rather than changing the run-off rate: the run-off rate only
// counts a loan once it has gone 6+ months without trail, so recent drops read as
// active and the figure stays low while the book is shrinking. A broker who
// measures "how many loans did I have then versus now" cannot reconcile to it.
// This card is that measure. The run-off definition and its tiers are untouched.

import React from 'react';
import { Scale } from 'lucide-react';
import StatCard from '../common/StatCard';
import { cardHelp } from '../../config/helpContent';
import { formatLoanBook, formatNumber } from '../../utils/formatters';
import type { TrailBookChange } from '../../hooks/useTrailRunOffPercentage';

const signed = (pct: number): string => `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;

/** Headline: change by balance; falls back to change by loan count when balances are hidden. */
export function formatBookChangeValue(change: TrailBookChange | null): string {
  if (!change) return '--';
  if (change.balanceChangePct !== null && change.balanceChangePct !== undefined) return signed(change.balanceChangePct);
  if (change.loanCountChangePct !== null && change.loanCountChangePct !== undefined) return signed(change.loanCountChangePct);
  return '--';
}

/** Subtitle: the two absolute figures the percentage was computed from. */
export function formatBookChangeSubtitle(change: TrailBookChange | null): string | undefined {
  if (!change) return undefined;
  const loans = `${formatNumber(change.openingLoans)} to ${formatNumber(change.closingLoans)} loans`;
  // Balances are stripped server-side for hidden-commission / hidden-loan-value
  // callers, so they may be absent even when the counts are present.
  const hasBalances =
    typeof change.openingBalanceCents === 'number' && typeof change.closingBalanceCents === 'number';
  if (!hasBalances) return loans;
  return `${loans}, ${formatLoanBook(change.openingBalanceCents)} to ${formatLoanBook(change.closingBalanceCents)}`;
}

interface Props {
  change: TrailBookChange | null;
  periodLabel: string;
  loading: boolean;
}

const TrailBookChangeStatCard: React.FC<Props> = ({ change, periodLabel, loading }) => {
  const value = formatBookChangeValue(change);
  const shrinking =
    change !== null &&
    ((change.balanceChangePct ?? change.loanCountChangePct ?? 0) < 0);
  return (
    <StatCard
      title="Trail Book Change"
      value={value}
      periodLabel={periodLabel}
      subtitle={formatBookChangeSubtitle(change)}
      icon={<Scale size={24} />}
      gradientColor={shrinking ? '#f59e0b' : '#22c55e'}
      isUpdating={loading}
      helpPageKey="trailRunOff"
      helpKey="trailBookChange"
      helpTooltip={cardHelp('trailRunOff', 'trailBookChange')}
    />
  );
};

export default TrailBookChangeStatCard;
